import type { SupabaseClient } from '@supabase/supabase-js';

// 캠페인 착지 기록 (2026-09-17) — 카카오 채널 메시지 등 외부 링크의 ?utm_campaign=… 을 붙잡아
// 로그인된 사용자와 묶어 서버(RPC record_campaign_visit → campaign_visits)에 1회 남긴다.
// 1) captureCampaignFromUrl: 앱이 뜨는 순간(라우터가 /auth 로 보내며 쿼리를 지우기 전) UTM을 localStorage에 보관
// 2) reportCampaignVisit: 세션이 생겼을 때(이미 로그인·나중에 로그인 모두) 보관분을 서버에 보고하고 지운다
// 실패는 전부 삼킨다 — 이 기능이 앱을 막으면 안 된다.

const PENDING_KEY = 'sdl_campaign_pending';
const REPORTED_KEY = 'sdl_campaign_reported';

interface PendingCampaign { campaign: string; source: string | null; medium: string | null; landedAt: string }

export function captureCampaignFromUrl(): void {
    try {
        const params = new URLSearchParams(window.location.search);
        const campaign = params.get('utm_campaign')?.trim();
        if (!campaign) return;
        const pending: PendingCampaign = {
            campaign: campaign.slice(0, 100),
            source: params.get('utm_source')?.trim().slice(0, 50) ?? null,
            medium: params.get('utm_medium')?.trim().slice(0, 50) ?? null,
            landedAt: new Date().toISOString(),
        };
        localStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    } catch { /* 프라이빗 모드·저장 불가 — 기록 없이 진행 */ }
}

let reporting = false;

export async function reportCampaignVisit(supabase: SupabaseClient): Promise<void> {
    if (reporting) return;
    try {
        const raw = localStorage.getItem(PENDING_KEY);
        if (!raw) return;
        const pending = JSON.parse(raw) as PendingCampaign;
        if (!pending?.campaign) { localStorage.removeItem(PENDING_KEY); return; }
        const reported: string[] = JSON.parse(localStorage.getItem(REPORTED_KEY) || '[]');
        if (reported.includes(pending.campaign)) { localStorage.removeItem(PENDING_KEY); return; }
        reporting = true;
        const { error } = await supabase.rpc('record_campaign_visit', {
            p_campaign: pending.campaign,
            p_source: pending.source,
            p_medium: pending.medium,
            p_landed_at: pending.landedAt,
        });
        if (!error) {
            reported.push(pending.campaign);
            localStorage.setItem(REPORTED_KEY, JSON.stringify(reported.slice(-20)));
            localStorage.removeItem(PENDING_KEY);
        }
    } catch { /* 네트워크·권한 오류 — 다음 세션에서 다시 시도 */ }
    finally { reporting = false; }
}

// 푸시 누름 기록 (2026-10-22 대표 7864 — 손님 한 명 한 명이 푸시로 들어왔는지 알기)
// 서비스워커(public/sw.js)가 서버 푸시를 누른 주소에 pushclick=1과 캠페인 번호 pc=…를 붙인다.
// 1) capturePushClickFromUrl: 앱이 뜨는 순간 표식을 localStorage에 보관(라우터가 쿼리를 지우기 전)
// 2) reportPushClick: 세션이 생기면(이미 로그인·30분 안에 로그인) 서버(RPC record_push_click → push_clicks)에 1번 남기고 지운다
// 실패는 전부 삼킨다 — 이 기능이 앱을 막으면 안 된다.

const PUSH_PENDING_KEY = 'sdl_pushclick_pending';
const PUSH_PENDING_TTL_MS = 30 * 60 * 1000;
const CAMPAIGN_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface PendingPushClick { campaignId: string | null; clickedAt: string; path: string }

export function capturePushClickFromUrl(): void {
    try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('pushclick') !== '1') return;
        const pc = params.get('pc')?.trim() ?? '';
        const pending: PendingPushClick = {
            campaignId: CAMPAIGN_ID_RE.test(pc) ? pc : null,
            clickedAt: new Date().toISOString(),
            path: window.location.pathname.slice(0, 200),
        };
        localStorage.setItem(PUSH_PENDING_KEY, JSON.stringify(pending));
    } catch { /* 프라이빗 모드·저장 불가 — 기록 없이 진행 */ }
}

let reportingPush = false;

export async function reportPushClick(supabase: SupabaseClient): Promise<void> {
    if (reportingPush) return;
    try {
        const raw = localStorage.getItem(PUSH_PENDING_KEY);
        if (!raw) return;
        const pending = JSON.parse(raw) as PendingPushClick;
        const age = Date.now() - Date.parse(pending?.clickedAt ?? '');
        if (!(age >= 0 && age <= PUSH_PENDING_TTL_MS)) { localStorage.removeItem(PUSH_PENDING_KEY); return; }
        reportingPush = true;
        const { error } = await supabase.rpc('record_push_click', {
            p_campaign_id: pending.campaignId,
            p_clicked_at: pending.clickedAt,
            p_path: pending.path,
        });
        if (!error) localStorage.removeItem(PUSH_PENDING_KEY);
    } catch { /* 네트워크·권한 오류 — 30분 안의 다음 세션에서 다시 시도 */ }
    finally { reportingPush = false; }
}
